import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { formatMoney } from '../utils/money';
import { normalizeRecurringFrequency, recurringFrequencyLabel } from '../utils/recurring';

export default function RecurringList({ tipo, modoOscuro, onGenerated }) {
  const { secureFetch } = useApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-300 text-gray-900 hover:bg-gray-50';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch(`/api/recurring?tipo=${tipo}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al cargar recurrentes');
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Error al cargar recurrentes');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [secureFetch, tipo]);

  useEffect(() => {
    load();
  }, [load]);

  const labelOf = (row) => {
    const p = row.payload || {};
    if (tipo === 'venta') return p.razonSocial || p.cliente || 'Venta recurrente';
    return p.concepto || p.proveedor || 'Gasto recurrente';
  };

  const amountOf = (row) => {
    const p = row.payload || {};
    return tipo === 'venta' ? Number(p.total) || 0 : Number(p.monto) || 0;
  };

  const currencyOf = (row) => (row.payload?.moneda || 'ARS');

  const act = async (id, fn) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message || 'Error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`}>
      <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}`}>
        <h2 className={`text-sm font-semibold ${textPri}`}>Gastos recurrentes</h2>
        <p className={`text-xs mt-0.5 ${textSec}`}>
          Mensuales o anuales (dominio, hosting). Se cargan solos en la fecha de próximo vencimiento.
        </p>
      </div>
      {error && (
        <p className={`px-4 py-2 text-xs ${modoOscuro ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
      )}
      {loading ? (
        <p className={`px-4 py-6 text-sm ${textMuted}`}>Cargando…</p>
      ) : items.length === 0 ? (
        <p className={`px-4 py-6 text-sm ${textMuted}`}>
          No hay plantillas. Al cargar un gasto, marcá «Crear plantilla recurrente» (mensual o anual).
        </p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((row) => (
            <div key={row._id || row.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={`text-sm font-semibold truncate ${textPri}`}>{labelOf(row)}</p>
                <p className={`text-[11px] ${textMuted}`}>
                  {formatMoney(amountOf(row), currencyOf(row))}
                  {' · '}
                  {recurringFrequencyLabel(row.frecuencia)}
                  {' · próxima '}
                  {String(row.nextRunDate || '').slice(0, 10) || '—'}
                  {row.active ? '' : ' · pausada'}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  disabled={busyId === (row._id || row.id) || !row.active}
                  onClick={() => act(row._id || row.id, async () => {
                    const res = await secureFetch(`/api/recurring/${row._id || row.id}/generate`, { method: 'POST' });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.message || 'No se pudo generar');
                    onGenerated?.(data);
                  })}
                  className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
                >
                  {normalizeRecurringFrequency(row.frecuencia) === 'yearly' ? 'Adelantar año' : 'Adelantar mes'}
                </button>
                <button
                  type="button"
                  disabled={busyId === (row._id || row.id)}
                  onClick={() => act(row._id || row.id, async () => {
                    const res = await secureFetch(`/api/recurring/${row._id || row.id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ active: !row.active }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
                  })}
                  className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold disabled:opacity-50 ${btnOut}`}
                >
                  {row.active ? 'Pausar' : 'Reanudar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
